import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { api } from '../api.js'
import { useAuth } from '../auth/AuthContext.jsx'

// Admin: list every account holder. Add/edit live on their own pages
// (/admin/users/new, /admin/users/:id/edit) — no inline forms.

export function AdminUsersPage() {
  const { t } = useTranslation()
  const { user, loading: authLoading } = useAuth()
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  function reload() {
    setLoading(true); setError(null)
    api.admin.users.list()
      .then((d) => setRows(d.users || []))
      .catch((e) => setError(e.code || 'server'))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    if (authLoading || !user || user.role !== 'admin') return
    reload()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user])

  async function deleteUser(u) {
    if (!confirm(t('admin_users.confirm_delete', { name: u.name }))) return
    try {
      await api.admin.users.delete(u.id)
      reload()
    } catch (e) {
      setError(e.code || 'server')
    }
  }

  if (authLoading) return <main className="container"><p>{t('common.loading')}</p></main>
  if (!user || user.role !== 'admin') {
    return <main className="container"><p className="error">{t('admin.errors.not_admin')}</p></main>
  }

  return (
    <main className="container">
      <p><Link to="/admin">← {t('admin.title')}</Link></p>
      <h1>{t('admin_users.title')}</h1>
      <p className="muted">{t('admin_users.intro')}</p>
      <p><Link to="/admin/users/new"><button type="button">+ {t('admin_users.add')}</button></Link></p>

      {error && (
        <p className="error">
          {t(`admin_users.errors.${error}`, { defaultValue: t(`auth.errors.${error}`, { defaultValue: error }) })}
        </p>
      )}
      {loading && <p>{t('common.loading')}</p>}

      {!loading && rows.length === 0 && (
        <p className="muted">{t('admin_users.empty_state')}</p>
      )}

      {!loading && rows.length > 0 && (
        <table className="results-table">
          <thead>
            <tr>
              <th>{t('admin_users.col_name')}</th>
              <th>{t('admin_users.col_email')}</th>
              <th>{t('admin_users.col_role')}</th>
              <th>{t('admin_users.col_participants')}</th>
              <th>{t('admin_users.col_actions')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((u) => {
              const isSelf = u.id === user.id
              return (
                <tr key={u.id}>
                  <td><Link to={`/admin/users/${u.id}`}>{u.name}</Link>{isSelf && <span className="muted small"> ({t('admin_users.you')})</span>}</td>
                  <td>{u.email}</td>
                  <td>{t(`admin_users.role_${u.role}`, { defaultValue: u.role })}</td>
                  <td>{u.participant_count}</td>
                  <td>
                    <Link to={`/admin/users/${u.id}/edit`} className="link-button">
                      {t('admin_users.edit')}
                    </Link>
                    <Link to={`/admin/users/${u.id}`} className="link-button" style={{ marginLeft: '0.5rem' }}>
                      {t('admin_users.manage_participants')}
                    </Link>
                    <button
                      type="button"
                      className="link-button danger"
                      style={{ marginLeft: '0.5rem' }}
                      onClick={() => deleteUser(u)}
                      disabled={isSelf}
                      title={isSelf ? t('admin_users.cannot_delete_self_hint') : ''}
                    >
                      {t('admin_users.delete')}
                    </button>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      )}
    </main>
  )
}
